// Duplicate-person prevention for the person CREATE form (#113).
//
// Create-only, deliberately: `runPersonMatchOnBlur` no-ops when the dialog is
// editing an existing person (see isEditingExistingPerson below).
//
// Modelled on cynqueModules/addressVerification.ts: a display-only field
// (`type: 'personMatchPanel'`) that the identity fields' `runOnBlur` populates.
// On blur we ask the server for existing persons whose identity keys intersect
// what's been typed (GET /api/persons/match, suggest-only), and write the
// ranked candidates onto the panel field so EntityMaskForm can render a
// "did you mean…?" prompt. Nothing here writes data or blocks the save.

import { debounce } from 'lodash'

import { isEditingExistingData } from '~/cynqueModules/componentHelpers/entityDialog/dialogMode'
import { getPersonById, matchPersons } from '~/services/persons'
import { useEntityDialogStore } from '~/stores/entityDialog'
import type { IEntityMaskData, TEntityId } from '~/types/models'
import type { IPersonMatchCandidate } from '~/types/person/models'

export const PERSON_MATCH_PANEL_FIELD_NAME = 'personMatchPanel'

/**
 * `runOnChanged` variant: fire the match while typing, debounced so we don't hit
 * the server on every keystroke. Because `setSubentityValuesFromEntity` runs a
 * field's `runOnChanged` after applying a pre-filled value, this also fires when
 * the NEW-person dialog opens pre-filled from the picker text — so the panel
 * appears shortly after open, not only after a manual blur. #113
 */
export const runPersonMatchDebounced = debounce(
  (ifield: IEntityMaskData, inputFields: IEntityMaskData[]) => {
    void runPersonMatchOnBlur(ifield, inputFields)
  },
  300
)

/**
 * Split the name typed into the person picker into first/last so the NEW-person
 * dialog opens pre-filled. First whitespace-separated word is the first name,
 * the rest the last name; blank parts are omitted. #113
 */
export function personNamePrefillValues(
  typedText: string | undefined
): { fieldName: string; value: string }[] {
  const text = (typedText ?? '').trim()
  if (!text) {
    return []
  }
  const words = text.split(/\s+/)
  const firstName = words[0] ?? ''
  const lastName = words.slice(1).join(' ')
  const values: { fieldName: string; value: string }[] = []
  if (firstName) {
    values.push({ fieldName: 'firstName', value: firstName })
  }
  if (lastName) {
    values.push({ fieldName: 'lastName', value: lastName })
  }
  return values
}

/**
 * True when the open dialog is EDITING an existing person rather than creating
 * one. Mirrors EntityMaskForm's own `isEditing`
 * (`!!entityData && !isSubentityDialog`) off the same store, because the
 * `runOnBlur` / `runOnChanged` contract hands a field its own spec and the
 * sibling fields — not the component, and not the mode.
 *
 * ⚠ The subentity check is load-bearing, not defensive. `entityData` belongs to
 * the TOP-LEVEL dialog and stays set while a sub-dialog sits over it, but a
 * subentity dialog is always creating. Without the check, opening "NEW person"
 * from a contact you are EDITING would silently lose the panel — which is the
 * exact flow #113 was built for.
 */
function isEditingExistingPerson(): boolean {
  const store = useEntityDialogStore()
  if (store.showSubentityDialog) {
    return false
  }
  return isEditingExistingData({
    entityData: store.entityData,
    desiredAction: store.desiredAction ?? undefined,
  })
}

function fieldValue(
  inputFields: IEntityMaskData[],
  name: string
): string | undefined {
  const value = inputFields.find((f) => f.name === name)?.value
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

/**
 * A field's `runOnBlur`, installed on firstName/lastName/email. Looks up the
 * panel field by name and fills it with the current match candidates (or hides
 * it when there are none / nothing identifying has been typed).
 *
 * `_blurredField` is the field that blurred (the runOnBlur contract passes it);
 * we read all identity values off `inputFields` instead.
 */
export async function runPersonMatchOnBlur(
  _blurredField: IEntityMaskData,
  inputFields: IEntityMaskData[]
): Promise<void> {
  const panel = inputFields.find(
    (f) => f.name === PERSON_MATCH_PANEL_FIELD_NAME
  )
  if (!panel) {
    return
  }

  // Duplicate detection is a CREATE-time question only. When EDITING a person,
  // every identity field already matches that person, so the endpoint returns
  // the very record being edited (plus its near neighbours) and the panel
  // invites the user to navigate away from the edit they are in the middle of.
  // Bail before the request rather than before the render, so an edit makes no
  // /api/persons/match traffic at all.
  if (isEditingExistingPerson()) {
    panel.matchCandidates = []
    panel.unrendered = true
    return
  }

  const candidates = await fetchPersonMatchCandidates(inputFields)
  panel.matchCandidates = candidates
  // Same mechanism as the address panel: `unrendered` hides it until there is
  // something to say.
  panel.unrendered = candidates.length === 0
}

/**
 * Link an existing person into the parent record from the "NEW person" subentity
 * dialog (#113): hydrate the full person and hand it back to the parent field via
 * the same subentity-return path a freshly-created person would use, then close
 * the sub-dialog. So picking a candidate attaches that person to the contact
 * instead of minting a duplicate.
 */
export async function linkExistingPersonToParent(
  candidateId: TEntityId
): Promise<void> {
  const person = await getPersonById(candidateId)
  const store = useEntityDialogStore()
  store.setSubentityReturnValue(person)
  // ⚠ clearSubentityDialog, NOT setShowSubentityDialog(false). Every other
  // subentity close reaches the same teardown through the component —
  // EntityDialog.close() -> the propModel setter -> clearStoreValuesAndHideDialog()
  // -> this action — and hiding the dialog by the flag alone leaves subentityName,
  // subentityAfterRequestProcessor, subentityInputFieldsEntityNameOverride,
  // subentityParentObjectForCreate and the pre-fill values all still set, to leak
  // into the NEXT 'NEW'. This module has no component to call close() on, so it
  // calls the store action that close() ultimately reaches.
  // ✅ Safe after setSubentityReturnValue: clearSubentityDialog hides the dialog
  // and clears the subentity CONFIG, but neither it nor the clearCommon() it
  // awaits touches subentityReturnValue — so the parent still receives the person.
  await store.clearSubentityDialog()
}

/**
 * Split a single typed name (as entered in a person picker) into first/last:
 * first whitespace word is the first name, the rest the last name. Blank parts
 * are omitted. #113
 */
export function splitTypedName(text: string | undefined): {
  firstName?: string
  lastName?: string
} {
  const trimmed = (text ?? '').trim()
  if (!trimmed) {
    return {}
  }
  const words = trimmed.split(/\s+/)
  const firstName = words[0]
  const lastName = words.slice(1).join(' ')
  return {
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
  }
}

/**
 * Fuzzy-match the text typed into a person picker and return the set of matching
 * person ids, so the picker can surface near-misses ("Rob" → "Robert Smith")
 * the client-side substring filter would hide. Returns an empty set for blank
 * input. #113
 */
export async function matchPersonIdsBySearch(
  searchText: string | undefined
): Promise<Set<string>> {
  const { firstName, lastName } = splitTypedName(searchText)
  if (!firstName && !lastName) {
    return new Set()
  }
  const candidates = await matchPersons({ firstName, lastName })
  return new Set(candidates.map((c) => String(c.id)))
}

/** Read the identity fields and query the match endpoint. Exposed for testing. */
export async function fetchPersonMatchCandidates(
  inputFields: IEntityMaskData[]
): Promise<IPersonMatchCandidate[]> {
  const input = {
    firstName: fieldValue(inputFields, 'firstName'),
    lastName: fieldValue(inputFields, 'lastName'),
    email: fieldValue(inputFields, 'email'),
    phone: fieldValue(inputFields, 'phone'),
  }
  if (!input.firstName && !input.lastName && !input.email && !input.phone) {
    return []
  }
  return matchPersons(input)
}
